import { useCallback, useEffect, useState } from 'react';
import type { PlayEventMatch } from '@court-sync/shared';
import { Trophy } from 'lucide-react';
import { PublicOrgConsumerPortalPage } from '@/components/layout/PublicOrgConsumerPortalPage';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { playEventService } from '@/services/playEvent';

export default function MemberMyMatchesPage() {
  const orgSlug = useOrgSlug();
  const [matches, setMatches] = useState<PlayEventMatch[]>([]);
  const [loading, setLoading] = useState(true);

  const loadMatches = useCallback(async () => {
    if (!orgSlug) return;
    setLoading(true);
    try {
      const res = await playEventService.getMyMatches(orgSlug);
      setMatches(res.success && res.data ? res.data : []);
    } finally {
      setLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    void loadMatches();
  }, [loadMatches]);

  return (
    <PublicOrgConsumerPortalPage
      title="My matches"
      description="Upcoming and recent play event matches across the club."
    >
      {loading ? (
        <div className="flex min-h-[160px] items-center justify-center" aria-busy="true">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
        </div>
      ) : matches.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Trophy className="h-5 w-5 text-amber-500" aria-hidden />
              No matches yet
            </CardTitle>
            <CardDescription>
              Join a play event from the club play calendar to see your match schedule here.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <ul className="space-y-3">
          {matches.map((match) => (
            <li key={match.id}>
              <Card>
                <CardContent className="py-4 text-sm">
                  <p className="font-medium">Round {match.roundNumber}</p>
                  <p className="text-muted-foreground capitalize">{match.result.replace(/_/g, ' ')}</p>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </PublicOrgConsumerPortalPage>
  );
}
